import { useBoardsActions } from "app/containers/Features/containers/Boards/components/useBoardsActions";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Links } from "../../../helpers";

interface LinkItem {
  name: string;
  title: string;
  path: string;
  icon?: React.ReactElement;
  children?: LinkItem[];
  action?: string;
}

export default function ProjectTabs(): React.ReactElement {
  const navigate = useNavigate();
  const params = useParams<{ id: string; projectId: string }>();
  const { id, projectId } = params;
  const location = useLocation();
  const { fetchProjectSettings } = useBoardsActions(projectId as string);

  const TABS = useMemo(() => {
    const projectLink = (Links as LinkItem[]).find(
      (l) => l.name === "Projects",
    );

    if (projectLink?.children) {
      return projectLink.children.map((child) => ({
        key: child.name,
        title: child.title,
        icon: child.icon,
      }));
    }
    return [];
  }, []);

  const activeTab = useMemo(() => {
    const pathSegments = location.pathname.split("/");
    return pathSegments[pathSegments.length - 1];
  }, [location.pathname]);

  const handleTabClick = useCallback(
    (tabKey: string) => {
      if (!id || !projectId) return;
      navigate(`/app/team/${id}/projects/${projectId}/${tabKey}`, {
        replace: true,
      });
    },
    [id, projectId, navigate],
  );
  useEffect(() => {
    if (projectId) {
      fetchProjectSettings(projectId);
    }

    return () => {};
  }, [projectId]);

  return (
    <div className="relative">
      {/* Tab Bar */}
      <div className="flex flex-row bg-white shadow-md w-fit  mx-6 sticky top-0 z-10">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              className={clsx(
                "relative flex flex-row items-center gap-2 px-4 uppercase text-sm font-normal leading-[21px] py-2 hover:bg-gray-400 group border-b-2 ",
                isActive ? "border-primary" : "border-gray-200",
              )}
            >
              {tab.icon}
              <span
                className={clsx(
                  "text-nowrap",
                  isActive ? "font-semibold text-primary" : "text-secondary",
                )}
              >
                {tab.title}
              </span>
            </button>
          );
        })}
      </div>

      {/* Content Area */}
      <div className="mt-4 shadow-sm overflow-auto relative h-[calc(100vh-180px)]">
        <Outlet />
      </div>
    </div>
  );
}
